import { put, takeLatest, takeEvery, select, delay } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import config from '@xto10x/core/lib/config';
import auth from '@xto10x/core/lib/cognito.auth';
import { isSignedInWithGoogle } from '@xto10x/oauth2/lib/getSignedInWith';
import isAuthenticatedOauth2 from '@xto10x/oauth2/lib/isAuthenticated';
import googleOauth from '@xto10x/oauth2/lib/googleOauth';
import userApi from 'services/userApi';
import orgApi from 'services/orgApi';
import teamsApi from 'services/teamsApi';
import configApi from 'services/configApi';
import objectivesApi from 'services/objectivesApi';
import storage from '@xto10x/storage/lib';
import {
  getSelectedTeam,
  hasOKRModuleAccess,
  getCustomRangeParameters,
  getAbsoluteHomePath,
  getCachedConfig,
  isExecutionCycle
} from 'utils/app.utils';
import { getActiveObjectiveCycleIndex } from 'utils/objectives.utils';
import sortTagsByUsage from 'utils/tag.utils';
import { getValueFromQueryParam } from '@xto10x/core/lib/app.utils';
import {
  QueryParam,
  StorageKey,
  UserStatus,
  UrlOrgDomainExceptions,
  UserConfigType,
  AlertActionTypes,
  UserSubscriptionTypes,
  HeaderLeftTabs,
  ConfigCacheKeys
} from 'utils/constants';
import analytics from '@xto10x/core/lib/analytics';
import getCredentials from '@xto10x/oauth2/lib/getCredentials';
import {
  getCurrentUserInfoSelector,
  getIsAuthenticatedSelector,
  getOrgAccessibleModules,
  getAccessibleModules
} from 'containers/App/store/reducer';
import { AnalyticsCategory } from 'utils/analytics.constants';
import { getObjectiveCyclesSelector } from 'containers/Header/store/reducer';
import { getFetchedOKRModuleData, getOrgDataSelector } from './reducer';
import {
  actions,
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  saveUserInfoAction,
  saveOrgData,
  saveOrgDataInState,
  saveCompanyTeamsList,
  saveUserTeamsData,
  fetchTeamsData,
  signOutAction,
  setDiffOrgData,
  clearAllData,
  saveTooltipInfo,
  clearAllReducer,
  saveUserOrgsAction,
  fetchUserOrgsAction,
  registerUserAccess,
  fetchCompanyTagsList,
  saveCompanyTagsList,
  saveManageableTags,
  saveRecentlyCreatedTeam,
  saveGoalStrengthScores,
  fetchOrgSettings,
  setObjectiveConfig,
  setOrgDirectReportees,
  fetchUserInfoAction,
  fetchOrgData,
  setOrgHierarchy,
  setAccessibleModules,
  setOrgAccessibleModules,
  setUserRoles,
  fetchUserModules,
  fetchOrgModules,
  setFetchedOKRModuleData,
  saveIsUserManager,
  saveIsUserLineManager,
  setCurrentUserHierarchyLevel,
  saveIsUserHrbp,
  setCurrentUserHrbpDetails,
  setCurrentUserReporteesByPersona,
  setObjectiveOperationsList,
  fetchUserPreferences
} from './actions';
import {
  fetchObjectiveCycleList,
  saveSelectedTeam
} from '../../Header/store/actions';

import { saveOrgSelectionAction } from '../../Authentication/store/actions';

function* fetchUserInfoWorker() {
  try {
    yield put(showAppLoading());
    const userInfo = storage.getDataFromLocal(StorageKey.USER_INFO);
    if (userInfo && userInfo.user_reference_id) {
      const userData = yield userApi
        .getUserByReferenceID(userInfo.user_reference_id)
        .then(res => res.data);

      // TODO : handle saving is_user_manager more safely
      yield put(saveIsUserManager(userData.is_user_manager));
      yield put(saveIsUserHrbp(userData.is_hrbp));
      yield put(
        saveIsUserLineManager(
          !!userData?.hierarchy_users?.hierarchy_reports_with_level?.['2']
        )
      );
      if (
        userData?.hierarchy_users?.hierarchy_managers?.length ||
        userData?.hierarchy_users?.hierarchy_managers?.length === 0
      )
        yield put(
          setCurrentUserHierarchyLevel(
            userData.hierarchy_users.hierarchy_managers.length + 1
          )
        );
      yield put(fetchUserModules(userInfo.user_reference_id));
      if (userData.hierarchy_users) {
        const {
          hierarchy_managers_with_level,
          hierarchy_reports_with_level
        } = userData.hierarchy_users;
        yield put(
          setOrgHierarchy(
            (hierarchy_managers_with_level &&
              Object.values(hierarchy_managers_with_level).reduce(
                (acc, val) => {
                  acc.push(val);
                  return acc;
                },
                []
              )) ||
              [],
            (hierarchy_reports_with_level &&
              Object.values(hierarchy_reports_with_level).reduce((acc, val) => {
                acc.push(...val);
                return acc;
              }, [])) ||
              []
          )
        );
        yield put(
          setOrgDirectReportees(hierarchy_reports_with_level?.['1'] || [])
        );
      }
      // if the user is logged in but the status is still pending, mark user as ACTIVE, else just save user info
      if (userData.status === UserStatus.PENDING) {
        analytics.trackEvent(
          AnalyticsCategory.account,
          'user_updated_from_pending_to_active'
        );
        yield userApi.updateUser({
          user_reference_id: userInfo.user_reference_id,
          status: UserStatus.ACTIVE
        });
      }
    } else {
      // Track analytics
      analytics.trackError(
        'user_reference_id is null or empty in local while fetching user info.'
      );
    }

    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_user_info_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchUserOrgsWorker() {
  try {
    yield put(showAppLoading());
    const userInfo = yield userApi.getUserInfo().then(res => res.data);
    yield put(saveUserOrgsAction(userInfo.orgs));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_user_orgs_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchOrgSettingsWorker() {
  try {
    const settings = yield userApi.getSettings().then(res => res.data);
    yield put(setObjectiveConfig(settings.attributes.objective_settings));
    yield put(setObjectiveOperationsList(settings.operation_master_list));
    storage.saveDataInLocal(StorageKey.ORG_SETTINGS, settings?.attributes);
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_org_settings_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateOrgSettingsWorker({ requestBody, isEmployeeSettingsUpdated }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.updateOrgSettings(requestBody);
    yield put(setObjectiveConfig(data.attributes.objective_settings));
    yield put(setObjectiveOperationsList(data.operation_master_list));
    storage.saveDataInLocal(StorageKey.ORG_SETTINGS, data?.attributes);

    if (isEmployeeSettingsUpdated)
      yield put(
        showAlertMessage('success', 'Manager and Employee settings updated!')
      );
    else
      yield put(showAlertMessage('success', 'Settings updated successfully!'));
    // analytics.trackEvent('update_settings');
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('An error occurred', e.message));
    analytics.trackError('update_org_settings_failed', e);
  }
}

function* fetchOrgDirectReporteesWorker() {
  try {
    yield put(showAppLoading());
    const currentUserInfo = yield select(getCurrentUserInfoSelector);
    const {
      hierarchy_reports_with_level
    } = yield objectivesApi
      .getOrgHierarchy(currentUserInfo.user_reference_id)
      .then(res => res.data);
    yield put(setOrgDirectReportees(hierarchy_reports_with_level?.['1'] || []));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_org_direct_reportees_worker', e);
  }
}

const revokeGoogleSignInToken = async () => {
  try {
    const isAuthenticated = await isAuthenticatedOauth2();
    if (isSignedInWithGoogle() && isAuthenticated)
      await googleOauth.revokeRefreshGoogleAuthToken();
  } catch (err) {
    //
  }
};

function* signOutWorker({ showToast }) {
  try {
    const currentOrg = yield select(getOrgDataSelector);
    yield put(showAppLoading());
    yield auth.signOut().catch(() => null);
    yield revokeGoogleSignInToken();
    yield put(clearAllData());
    yield put(delay(300));
    if (showToast) yield put(showAlertMessage('success', 'Logged out'));
    if (currentOrg?.sub_domain) {
      window.location.href = `${
        config.loginUrl
      }?redirect_url=${encodeURIComponent(
        `${window.location.origin}/${currentOrg.sub_domain}`
      )}&app=PeopleCues&stopAutoLogin=true`;
    } else {
      window.location.href = `${
        config.loginUrl
      }?redirect_url=${encodeURIComponent(
        `${window.location.origin}`
      )}&app=PeopleCues&stopAutoLogin=true`;
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('sign_out_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* initialCallWorker({ hideLoader }) {
  try {
    if (!hideLoader) yield put(showAppLoading());
    const user_reference_id = storage.getDataFromLocal(
      StorageKey.REQUESTER_USER_ID
    );
    let userSubscribedOrgs = storage.getDataFromLocal(StorageKey.USER_ORGS);
    if (
      !userSubscribedOrgs ||
      !Array.isArray(userSubscribedOrgs) ||
      userSubscribedOrgs.length === 0
    ) {
      yield put(fetchUserOrgsAction());
      userSubscribedOrgs = storage.getDataFromLocal(StorageKey.USER_ORGS);
    }
    let access_token = null;
    try {
      access_token = (yield getCredentials()).idToken;
    } catch (err) {
      //
    }
    const localOrgData = storage.getDataFromLocal(StorageKey.ORG_INFO);
    const localOrgSubDomain = localOrgData?.sub_domain;
    const urlOrgSubDomain = window.location.pathname.split('/')[1];
    const isAuthenticated = yield select(getIsAuthenticatedSelector);

    if (!user_reference_id) {
      // if user not logged in
      if (isAuthenticated) yield put(signOutAction());
      if (
        urlOrgSubDomain &&
        urlOrgSubDomain != null &&
        urlOrgSubDomain !== 'null' &&
        urlOrgSubDomain !== 'sso' &&
        !UrlOrgDomainExceptions.includes(urlOrgSubDomain)
      ) {
        // if url has org name
        let orgData;
        if (urlOrgSubDomain !== 'orgs')
          orgData = userSubscribedOrgs.find(
            ({ sub_domain }) => sub_domain === urlOrgSubDomain
          );

        if (orgData && orgData.org_reference_id)
          yield put(saveOrgData(orgData));
        else yield put(signOutAction(false));
      }
    } else if (
      user_reference_id &&
      access_token &&
      urlOrgSubDomain === 'orgs'
    ) {
      return;
    } else if (user_reference_id && access_token) {
      // if user logged in
      if (
        urlOrgSubDomain &&
        urlOrgSubDomain != null &&
        urlOrgSubDomain !== 'null' &&
        urlOrgSubDomain !== 'undefined' &&
        urlOrgSubDomain !== 'orgs' &&
        urlOrgSubDomain !== 'home' &&
        urlOrgSubDomain !== 'sso'
      ) {
        try {
          const userData = yield userApi
            .getUserByReferenceID(user_reference_id)
            .then(({ data }) => data);

          // TODO : handle saving is_user_manager more safely

          yield put(saveIsUserManager(userData.is_user_manager));
          yield put(saveIsUserHrbp(userData.is_hrbp));
          if (userData?.hierarchy_users?.hierarchy_managers?.length)
            yield put(
              setCurrentUserHierarchyLevel(
                userData.hierarchy_users.hierarchy_managers.length + 1
              )
            );
          if (userData.status === UserStatus.PENDING) {
            yield userApi.updateUser({
              user_reference_id,
              status: UserStatus.ACTIVE
            });
            yield put(fetchUserModules(user_reference_id));
          } else if (userData.status === UserStatus.ACTIVE)
            yield put(fetchUserModules(user_reference_id));
          else yield put(signOutAction());
        } catch (err) {
          if (
            !localOrgSubDomain &&
            storage.getDataFromLocal(StorageKey.USER_ORGS)
          ) {
            yield put(replace('/orgs'));
            return;
          }
          yield put(clearAllData());
          return;
        }

        let urlOrgData = null;
        if (urlOrgSubDomain !== 'orgs') {
          urlOrgData = userSubscribedOrgs.find(
            ({ sub_domain }) => sub_domain === urlOrgSubDomain
          );
          yield put(saveOrgData(urlOrgData));
        }

        if (
          urlOrgData &&
          urlOrgData.org_reference_id &&
          localOrgData &&
          localOrgData.org_reference_id &&
          urlOrgData.org_reference_id !== localOrgData.org_reference_id
        ) {
          // if url org is different than local org
          yield put(setDiffOrgData(urlOrgData));
        } else if (localOrgData && localOrgData.org_reference_id) {
          // analytics.trackEvent('app_store_saga', 'fetching_user_info_start');
          const userInfo = yield userApi.getUserInfo().then(res => res.data);
          // analytics.trackEvent('app_store_saga', 'fetching_user_info_end');
          yield put(saveUserOrgsAction(userInfo.orgs));
          if (!window.location.pathname.includes(localOrgSubDomain))
            yield put(replace(`/${localOrgSubDomain}/objectives`));
        } else {
          yield put(replace('/orgs'));
        }
      } else if (localOrgSubDomain)
        yield put(replace(`/${localOrgSubDomain}/objectives`));
      else if (storage.getDataFromLocal(StorageKey.USER_ORGS) === null) {
        yield put(signOutAction(false));
        yield put(clearAllData(false));
        return;
      } else if (urlOrgSubDomain === 'orgs') return;
      else yield put(replace('/orgs'));
    } else {
      yield put(clearAllData());
    }

    if (!hideLoader) yield put(hideAppLoading());
  } catch (e) {
    if (!hideLoader) yield put(hideAppLoading());
    analytics.trackError('initial_call_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchOKRModuleDataWorker({ showLoading }) {
  try {
    const fetchedOKRModuleData = yield select(getFetchedOKRModuleData);
    if (fetchedOKRModuleData) return;
    const accessibleModules = yield select(getAccessibleModules);
    if (!hasOKRModuleAccess(accessibleModules)) return;
    if (showLoading) yield put(showAppLoading());
    yield put(
      fetchObjectiveCycleList({
        selectedObjCycleId: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
        burstCache: true,
        userDetailsApiV3: true
      })
    );
    yield put(
      fetchTeamsData('ORG', getValueFromQueryParam(QueryParam.SELECTED_TEAM))
    );
    yield put(fetchCompanyTagsList());
    yield put(fetchTeamsData('USER'));
    if (
      !getCachedConfig(
        ConfigCacheKeys.goalsStrengthSchema,
        StorageKey.GOALS_STRENGTH_SCHEMA
      )
    ) {
      const goalStrengthScoreSchema = yield configApi
        .getGlobalConfig('goals_strength_score_schema')
        .then(res => res.data.data)
        .catch(() => {});
      yield put(saveGoalStrengthScores(goalStrengthScoreSchema));
    }

    yield put(fetchOrgSettings());
    yield put(setFetchedOKRModuleData(true));
    if (showLoading) yield put(hideAppLoading());
  } catch (e) {
    if (showLoading) yield put(hideAppLoading());
    analytics.trackError('fetch_org_module_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchUserPreferencesWorker() {
  try {
    const { data } = yield configApi.getAllUserPreferences();
    storage.saveDataInLocal(
      StorageKey.ALL_USER_PREFERENCES,
      data.data.reduce((acc, val) => {
        acc[val.preference_type] = val.data;
        return acc;
      }, {})
    );
    document.dispatchEvent(new CustomEvent('user_preferences_store_updated'));
  } catch (e) {
    analytics.trackError('fetch_user_preferences_worker', e);
  }
}

function* fetchAppDataWorker() {
  try {
    const currentUrlSubdomain = window.location.pathname.split('/')[1];
    yield put(showAppLoading());
    yield put(fetchOrgData(currentUrlSubdomain));
    yield put(fetchUserOrgsAction());
    yield put(fetchUserInfoAction());
    yield put(fetchUserPreferences());
    yield put(registerUserAccess());
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_app_data_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchTeamsDataWorker({ teamType, selectedTeamId }) {
  try {
    yield put(showAppLoading());
    let teams = null;
    if (teamType === 'ORG') {
      teams = yield teamsApi.getTeamsList(teamType).then(res => res.data.tags);
      yield put(saveSelectedTeam(getSelectedTeam(teams, selectedTeamId)));
      yield put(saveCompanyTeamsList(teams));
    } else if (teamType === 'USER') {
      teams = yield teamsApi.getTeamsList(teamType).then(res => res.data.tags);
      yield put(saveUserTeamsData(teams));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_teams_data_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchCompanyTagsWorker() {
  try {
    yield put(showAppLoading());
    const tags = yield teamsApi
      .getTeamsList('ORG', true)
      .then(res => res.data.tags);
    yield put(saveCompanyTagsList(sortTagsByUsage(tags)));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_org_tags_data_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchManageableTagsWorker() {
  try {
    yield put(showAppLoading());
    const tags = yield teamsApi.getManageTagsList().then(res => res.data.tags);
    yield put(saveManageableTags(tags));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_manageable_tags_data_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateTagsWorker({ requestBody, callback }) {
  try {
    yield put(showAppLoading());
    const tags = yield teamsApi
      .updateTagsBulk(requestBody)
      .then(res => res.data.tags);
    if (tags?.length)
      yield put(
        showAlertMessage(
          AlertActionTypes.NO_ICON,
          '🏷️ Your tags have been modified'
        )
      );
    yield put(fetchCompanyTagsList());
    if (callback) callback(true);
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_manageable_tags_data_worker', e);
    yield put(showAlertMessage('error', e.message));
    callback(false);
  }
}

function* createTeamRequestWorker({
  label,
  description,
  owners,
  followers,
  selectedTeam,
  isTag,
  callback
}) {
  try {
    yield put(showAppLoading());
    let data = null;
    if (isTag) {
      data = (yield teamsApi.createTeamRequest({ label, tag: isTag })).data;
      yield put(fetchCompanyTagsList());
    } else {
      data = (yield teamsApi.createTeamRequest({
        label,
        description,
        owners,
        followers,
        tag: isTag
      })).data;
      yield put(saveRecentlyCreatedTeam(data?.tag_id));
      yield put(fetchTeamsData('ORG', selectedTeam));
      yield put(fetchTeamsData('USER'));
      yield put(showAlertMessage('success', `${label} team created.`));
    }
    if (callback) {
      callback(data);
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('create_team_request_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchOrgDataWorker({ subdomain }) {
  try {
    const userInfo = yield userApi.getUserInfo().then(res => res.data);
    const userSubscribedOrgs = userInfo.orgs;
    if (
      subdomain == null ||
      subdomain === 'null' ||
      subdomain === 'sso' ||
      subdomain === 'orgs'
    )
      return;
    yield put(showAppLoading());
    const orgData = userSubscribedOrgs.find(
      ({ sub_domain }) => sub_domain === subdomain
    );
    if (orgData && orgData.org_reference_id) {
      yield put(saveOrgData(orgData));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(clearAllData(true));
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_org_data_worker', e);
  }
}

function* confirmChangeOrgWorker({ subdomain }) {
  let userSubscribedOrgs = storage.getDataFromLocal(StorageKey.USER_ORGS);
  if (
    !userSubscribedOrgs ||
    !Array.isArray(userSubscribedOrgs) ||
    userSubscribedOrgs.length === 0
  ) {
    yield put(fetchUserOrgsAction());
    userSubscribedOrgs = storage.getDataFromLocal(StorageKey.USER_ORGS);
  }
  if (
    subdomain == null ||
    subdomain === 'null' ||
    subdomain === 'sso' ||
    subdomain === 'orgs'
  )
    return;

  try {
    yield put(showAppLoading());
    const orgData = userSubscribedOrgs.find(
      ({ sub_domain }) => sub_domain === subdomain
    );
    if (orgData && orgData.org_reference_id) {
      yield put(saveOrgData(orgData));
      yield put(saveOrgSelectionAction(orgData));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('confirm_change_org_worker', e);
  }
}

function* clearAllDataWorker({ persistUserInfo }) {
  try {
    yield put(clearAllReducer());
    yield storage.clearAllStorage(persistUserInfo);
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('clear_all_data_worker', e);
  }
}

function* clearOrgDataWorker() {
  try {
    yield put(saveOrgData({}));
    yield storage.clearOrgStorage();
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('clear_org_data_worker', e);
  }
}

function* showToolTipActionWorker({ requestBody }) {
  try {
    const storedUserPreferences = storage.getDataFromLocal(
      StorageKey.ALL_USER_PREFERENCES
    );
    const viewed = !!storedUserPreferences?.[
      UserConfigType[requestBody.tooltip_type]
    ]?.viewed;
    const tooltipConfig = {
      show_tool_tip: false,
      ...requestBody
    };
    if (!viewed) {
      const { data: created } = yield configApi.postUserConfig(
        { viewed: true },
        UserConfigType[requestBody.tooltip_type]
      );
      if (created === 'created') tooltipConfig.show_tool_tip = true;
    }
    yield put(saveTooltipInfo(tooltipConfig));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
  }
}

function* registerUserAccessWorker() {
  try {
    const orgAccessibleModules = yield select(getOrgAccessibleModules);
    if (
      orgAccessibleModules &&
      orgAccessibleModules?.includes(UserSubscriptionTypes.ADVANCED) &&
      window.location.pathname.split('/').length > 2 &&
      window.location.pathname.split('/')[2] === 'objectives'
    )
      yield userApi.registerUserAccess();
  } catch (e) {
    analytics.trackError('register_user_access', e);
  }
}

function* saveOrgDataWorker({ org }) {
  storage.saveDataInLocal(StorageKey.ORG_INFO, org);
  if (org?.org_reference_id) {
    yield put(fetchOrgModules());
  }
  yield put(saveOrgDataInState(org));
}

function* handleApiCallErrorWorker({ error /* actionData */ }) {
  try {
    /* if (
      error.message?.includes(
        'Please check your network connection and try again'
      )
    ) {
      const alertAction = {
        actionText: 'Retry',
        actionType: ToastActionType.API_CALL_NETWORK_ERROR_FAILURE,
        actionData
      };
      yield put(showAlertMessage('error', error.message, alertAction));
    } else */
    yield put(showAlertMessage('error', error.message));
  } catch (e) {
    analytics.trackError('handle_api_call_error_worker', e);
  }
}

function* fetchUserModulesWorker({ user_reference_id }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getUserModules(user_reference_id);
    const roles = {};
    const accessibleModules = [];
    const visibleModules = [];
    data.forEach(d => {
      if (d.visible_modules) {
        visibleModules.push(...d.visible_modules);
      }
      if (d.accessible_modules) {
        accessibleModules.push(...d.accessible_modules);
      }
      roles[d.product_reference_id] = d.role;
    });
    yield put(setAccessibleModules(accessibleModules, visibleModules));
    yield put(setUserRoles(roles));
    const objInfo = data.filter(d => d.product_reference_id === 'okr');
    yield put(saveUserInfoAction(objInfo?.length ? objInfo[0] : data[0])); // user okr user role if available for backward compatibility. If okr not present, take first as default
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_user_modules_worker', e);
  }
}

function* fetchOrgModulesWorker() {
  try {
    yield put(showAppLoading());
    const { data } = yield orgApi.getOrgAccessibleModules();
    yield put(setOrgAccessibleModules(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('fetch_org_modules_worker', e);
  }
}

function* handleHomeButtonClickWorker() {
  try {
    const tm =
      getValueFromQueryParam(QueryParam.SELECTED_TEAM) ||
      HeaderLeftTabs[0].tag_id;
    const ob = getValueFromQueryParam(QueryParam.SELECTED_OBJECTIVE);
    let accessibleModules = yield select(getAccessibleModules);
    let objectiveCycles = yield select(getObjectiveCyclesSelector);
    if (!accessibleModules) {
      yield put(fetchUserModules());
      accessibleModules = yield select(getAccessibleModules);
    }
    const [sd, ed] = getCustomRangeParameters();
    let customRangeParams = [
      {
        key: QueryParam.CUSTOM_DATE_RANGE_START_DATE,
        value: sd
      },
      {
        key: QueryParam.CUSTOM_DATE_RANGE_END_DATE,
        value: ed
      }
    ];
    const hasAdvancedOkrAccess = accessibleModules.includes(
      UserSubscriptionTypes.ADVANCED
    );
    if (hasAdvancedOkrAccess) {
      if (!objectiveCycles || objectiveCycles.length === 0) {
        objectiveCycles = yield objectivesApi
          .getObjectiveCycles()
          .then(res => res.data.objective_cycles)
          .catch(() => []);
      }
      if (objectiveCycles && objectiveCycles.length > 0) {
        const executionCycles = objectiveCycles.filter(c =>
          isExecutionCycle(c.cycle_type)
        );
        const activeObjectiveCycleIndex = getActiveObjectiveCycleIndex(
          executionCycles || [],
          null
        );
        if (activeObjectiveCycleIndex > -1) {
          customRangeParams = [
            {
              key: QueryParam.CUSTOM_DATE_RANGE_START_DATE,
              value: new Date(
                executionCycles[activeObjectiveCycleIndex]?.start_date
              ).getTime()
            },
            {
              key: QueryParam.CUSTOM_DATE_RANGE_END_DATE,
              value: new Date(
                executionCycles[activeObjectiveCycleIndex]?.end_date
              ).getTime()
            }
          ];
        }
      }
    }
    const args = [
      {
        key: QueryParam.SELECTED_TEAM,
        value: tm
      },
      {
        key: QueryParam.SELECTED_OBJECTIVE,
        value: ob
      }
    ];
    args.splice(1, 0, ...customRangeParams);
    const path = getAbsoluteHomePath(...args);
    document.dispatchEvent(
      new CustomEvent('navigate_to_homepage', { detail: { path } })
    );
  } catch (err) {
    analytics.trackError('navigate_to_home_path', err);
  }
}

function* fetchCurrentUserHrbpDetailsWorker({ userReferenceId }) {
  try {
    const { data } = yield userApi.getCurrentUserHrbpDetails(userReferenceId);
    yield put(setCurrentUserHrbpDetails(data));
  } catch (error) {
    analytics.trackError('current-user-hrbp-failed', error);
  }
}

function* fetchCurrentUserReporteesByPersonaWorker({ personas }) {
  // personas : Personas[] -> Personas -> HRBP, ADMIN etc
  try {
    const personaQS = personas.join(',');
    const { data } = yield userApi.getUserReporteesByPersona(personaQS);

    yield put(setCurrentUserReporteesByPersona(data?.persona_reportees));
  } catch (error) {
    analytics.trackError('current-user-hrbp-failed', error);
  }
}

export default function* createTeamRequestWatcher() {
  yield takeLatest(actions.FETCH_USER_INFO_ACTION, fetchUserInfoWorker);
  yield takeLatest(actions.FETCH_ORG_SETTINGS, fetchOrgSettingsWorker);
  yield takeLatest(actions.UPDATE_ORG_SETTINGS, updateOrgSettingsWorker);
  yield takeLatest(actions.SIGN_OUT_ACTION, signOutWorker);
  yield takeLatest(actions.INITIAL_CALL, initialCallWorker);
  yield takeLatest(actions.FETCH_OKR_MODULE_DATA, fetchOKRModuleDataWorker);
  yield takeLatest(actions.FETCH_APP_DATA, fetchAppDataWorker);
  yield takeEvery(actions.FETCH_TEAMS_DATA, fetchTeamsDataWorker);
  yield takeEvery(actions.CREATE_TEAM_REQUEST, createTeamRequestWorker);
  yield takeEvery(actions.CREATE_TAG_REQUEST, createTeamRequestWorker);

  yield takeLatest(actions.FETCH_ORG_DATA, fetchOrgDataWorker);
  yield takeLatest(actions.CONFIRM_CHANGE_ORG, confirmChangeOrgWorker);
  yield takeLatest(actions.CLEAR_ALL_DATA, clearAllDataWorker);
  yield takeLatest(
    actions.SHOW_FEATURE_TOOLTIP_ACTION,
    showToolTipActionWorker
  );
  yield takeLatest(actions.CLEAR_ORG_DATA, clearOrgDataWorker);
  yield takeLatest(actions.FETCH_USER_ORGS_ACTION, fetchUserOrgsWorker);
  yield takeLatest(
    actions.REGISTER_USER_ACCESS_ACTION,
    registerUserAccessWorker
  );
  yield takeLatest(actions.SAVE_ORG_DATA, saveOrgDataWorker);
  yield takeLatest(actions.FETCH_COMPANY_TAGS_DATA, fetchCompanyTagsWorker);
  yield takeLatest(actions.FETCH_MANAGEABLE_TAGS, fetchManageableTagsWorker);
  yield takeLatest(actions.UPDATE_TAGS, updateTagsWorker);
  yield takeLatest(actions.HANDLE_API_CALL_ERROR, handleApiCallErrorWorker);
  yield takeLatest(
    actions.FETCH_ORG_DIRECT_REPORTEES,
    fetchOrgDirectReporteesWorker
  );
  yield takeLatest(actions.FETCH_USER_MODULES, fetchUserModulesWorker);
  yield takeLatest(actions.FETCH_ORG_ACCESSIBLE_MODULES, fetchOrgModulesWorker);
  yield takeLatest(
    actions.HANDLE_HOME_BUTTON_CLICK,
    handleHomeButtonClickWorker
  );
  yield takeLatest(
    actions.FETCH_CURRENT_USER_HRBP_DETAILS,
    fetchCurrentUserHrbpDetailsWorker
  );
  yield takeLatest(
    actions.FETCH_CURRENT_USER_REPORTEES_BY_PERSONA,
    fetchCurrentUserReporteesByPersonaWorker
  );
  yield takeLatest(actions.FETCH_USER_PREFERENCES, fetchUserPreferencesWorker);
}
